import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  CheckboxField,
  ChoiceRow,
  FormError,
  Hint,
  PrimaryButton,
  SelectField,
  TextAreaField,
  TextField,
} from '@/components/form/primitives';
import { categoryLabel, useCategoriesQuery, type ListingFeed } from '@/feed/hooks';
import type { MyOrganization } from '@/organization/hooks';

// Общая форма объявления (V1.3-e2) — одни поля для публикации (/post) и
// правки (/my/listings/:id/edit), чтобы не дублировать разметку. Различие
// режимов — проп `organizations`:
//   * задан (создание) — есть выбор «от имени организации» (при 2+) и ленты;
//   * не задан (правка) — этих контролов нет: организация и лента через
//     PATCH не меняются (VD9 контракта), статус — тоже (снятие — /archive).
// Ошибки/мутации остаются в страницах: форма отдаёт значения через onSubmit
// (строки уже trim'нуты) и показывает переданную ошибку.

export interface ListingFormValues {
  organizationId: string;
  feed: ListingFeed;
  categoryId: string;
  title: string;
  description: string;
  city: string;
  district: string;
  phone: string;
  /** «Телефон есть в WhatsApp» (№22) — всегда false без телефона. */
  whatsapp: boolean;
  telegram: string;
}

/** Контакты и город организации → дефолты полей формы (доводка V1 №16, №22). */
export function contactsFromOrganization(
  org: MyOrganization | undefined,
): Pick<ListingFormValues, 'city' | 'phone' | 'whatsapp' | 'telegram'> {
  return {
    city: org?.city ?? '',
    phone: org?.phone ?? '',
    whatsapp: org?.whatsapp ?? false,
    telegram: org?.telegram ?? '',
  };
}

export function ListingForm({
  organizations,
  initial,
  submitLabel,
  pending,
  error,
  onSubmit,
}: {
  /** Создание: verified-организации автора (выбор при 2+). Правка: undefined. */
  organizations?: MyOrganization[];
  initial?: Partial<ListingFormValues>;
  submitLabel: string;
  pending: boolean;
  error: string | null;
  onSubmit: (values: ListingFormValues) => void;
}): JSX.Element {
  const { t, i18n } = useTranslation();
  const categories = useCategoriesQuery().data?.items;

  const [organizationId, setOrganizationId] = useState(
    initial?.organizationId ?? organizations?.[0]?.id ?? '',
  );
  const [feed, setFeed] = useState<ListingFeed>(initial?.feed ?? 'offer');
  const [categoryId, setCategoryId] = useState(initial?.categoryId ?? '');
  const [title, setTitle] = useState(initial?.title ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [city, setCity] = useState(initial?.city ?? '');
  const [district, setDistrict] = useState(initial?.district ?? '');
  const [phone, setPhone] = useState(initial?.phone ?? '');
  const [whatsapp, setWhatsapp] = useState(initial?.whatsapp ?? false);
  const [telegram, setTelegram] = useState(initial?.telegram ?? '');
  // №22: флаг живёт у телефона — без номера снят и заблокирован (бэкенд:
  // true без contact_phone → 422 whatsapp_requires_phone).
  const hasPhone = phone.trim() !== '';
  const whatsappChecked = whatsapp && hasPhone;

  // Смена организации (2+ verified) — подставить её город и контакты (№16):
  // объявление наследует реквизиты организации, поля остаются редактируемыми.
  const selectOrganization = (nextId: string): void => {
    setOrganizationId(nextId);
    const org = organizations?.find((o) => o.id === nextId);
    if (org) {
      const defaults = contactsFromOrganization(org);
      setCity(defaults.city);
      setPhone(defaults.phone);
      setWhatsapp(defaults.whatsapp);
      setTelegram(defaults.telegram);
    }
  };

  // Пока категория не выбрана руками — первая из справочника (в прототипе
  // placeholder-состояния у селекта нет).
  const categoryValue = categoryId || (categories?.[0]?.id ?? '');

  const ready = Boolean(
    (organizations ? organizationId : true) &&
    categoryValue &&
    title.trim() &&
    description.trim() &&
    city.trim() &&
    (phone.trim() || telegram.trim()),
  );

  return (
    <>
      <FormError>{error}</FormError>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          onSubmit({
            organizationId,
            feed,
            categoryId: categoryValue,
            title: title.trim(),
            description: description.trim(),
            city: city.trim(),
            district: district.trim(),
            phone: phone.trim(),
            whatsapp: whatsappChecked,
            telegram: telegram.trim(),
          });
        }}
      >
        {organizations && organizations.length > 1 && (
          <SelectField
            label={t('post.form.organization')}
            value={organizationId}
            onChange={selectOrganization}
            name="organization_id"
            options={organizations.map((org) => ({ value: org.id, label: org.name }))}
          />
        )}
        {organizations && (
          <ChoiceRow<ListingFeed>
            value={feed}
            onChange={setFeed}
            label={t('post.form.feed')}
            options={[
              { value: 'demand', label: t('tabs.demand') },
              { value: 'offer', label: t('tabs.offer') },
              { value: 'notice', label: t('tabs.notice') },
            ]}
          />
        )}
        <SelectField
          label={t('post.form.category')}
          value={categoryValue}
          onChange={setCategoryId}
          name="category_id"
          options={(categories ?? []).map((category) => ({
            value: category.id,
            label: categoryLabel(categories, category.id, i18n.language),
          }))}
        />
        <TextField
          label={t('post.form.title')}
          value={title}
          onChange={setTitle}
          name="title"
          placeholder={t('post.form.titlePlaceholder')}
          maxLength={200}
        />
        <TextAreaField
          label={t('post.form.description')}
          value={description}
          onChange={setDescription}
          name="description"
          placeholder={t('post.form.descriptionPlaceholder')}
          maxLength={4000}
          rows={4}
        />
        <TextField
          label={t('post.form.city')}
          value={city}
          onChange={setCity}
          name="city"
          placeholder={t('post.form.cityPlaceholder')}
          maxLength={120}
        />
        <TextField
          label={t('post.form.district')}
          value={district}
          onChange={setDistrict}
          name="district"
          placeholder={t('post.form.districtPlaceholder')}
          maxLength={120}
        />
        <Hint>{t('post.form.contactsHint')}</Hint>
        <TextField
          label={t('post.form.phone')}
          value={phone}
          onChange={setPhone}
          type="tel"
          inputMode="tel"
          name="contact_phone"
          placeholder={t('post.form.phonePlaceholder')}
          maxLength={32}
        />
        <CheckboxField
          label={t('post.form.whatsapp')}
          checked={whatsappChecked}
          onChange={setWhatsapp}
          disabled={!hasPhone}
          name="contact_whatsapp"
        />
        <TextField
          label={t('post.form.telegram')}
          value={telegram}
          onChange={setTelegram}
          name="contact_telegram"
          placeholder={t('post.form.telegramPlaceholder')}
          maxLength={64}
        />
        <PrimaryButton disabled={pending || !ready}>{submitLabel}</PrimaryButton>
      </form>
    </>
  );
}
